// Backend SQLite nativo para offline-v2, mismo contrato que los backends web
// (WaSqliteIdbBackend / OpfsSahPool): name · open() · query · run · execute · executeSet
// (atómico) · transaction(fn) · persist() · reopen() · close() · enableEncryption().
//
// Motor: @capacitor-community/sqlite. `run`/`execute` pasan transaction=false cuando ya hay un
// beginTransaction() abierto (evita transacción anidada); executeSet siempre va con
// transaction=true (batch atómico, camino mutuamente excluyente con transaction(fn)).

import { CapacitorSQLite, SQLiteConnection } from '@capacitor-community/sqlite'
import { dbg, dbgError } from '../../logger'

const DB_NAME = 'binnacle_offline_v2'
const DB_VERSION = 1 // la migración de esquema la maneja runUserVersionMigration (PRAGMA user_version)

const sqliteConnection = new SQLiteConnection(CapacitorSQLite)

export class NativeCapacitorSqliteBackend {
  constructor () {
    this.name = '@capacitor-community/sqlite'
    this._db = null
    this._inTx = false
  }

  async open () {
    const isConsistent = (await sqliteConnection.checkConnectionsConsistency()).result
    const isConn = (await sqliteConnection.isConnection(DB_NAME, false)).result
    this._db = (isConsistent && isConn)
      ? await sqliteConnection.retrieveConnection(DB_NAME, false)
      : await sqliteConnection.createConnection(DB_NAME, false, 'no-encryption', DB_VERSION, false)
    await this._db.open()
    dbg('storage', `@capacitor-community/sqlite abierto (db=${DB_NAME})`)
    // Android usa WAL2 por defecto; no forzar journal_mode=WAL (sería downgrade).
    try {
      await this.execute('PRAGMA synchronous=NORMAL;')
    } catch { /* best-effort */ }
  }

  async query (sql, params = []) {
    const rs = await this._db.query(sql, params)
    return { values: (rs && rs.values) || [] }
  }

  async run (sql, params = []) {
    const rs = await this._db.run(sql, params, !this._inTx)
    const changes = (rs && rs.changes) || {}
    return {
      changes: { changes: changes.changes || 0, lastId: changes.lastId },
      lastInsertRowid: changes.lastId
    }
  }

  async execute (statements) {
    await this._db.execute(statements, !this._inTx)
    return {}
  }

  async executeSet (set) {
    const statements = (set || []).map(s => ({ statement: s.statement, values: s.values || [] }))
    try {
      const rs = await this._db.executeSet(statements, true)
      return { changes: { changes: (rs && rs.changes && rs.changes.changes) || statements.length } }
    } catch (err) {
      dbgError('storage', `executeSet FALLÓ -> ROLLBACK total (${statements.length} sentencias)`, err)
      throw err
    }
  }

  async transaction (fn) {
    await this._db.beginTransaction()
    this._inTx = true
    try {
      const result = await fn(this)
      await this._db.commitTransaction()
      return result
    } catch (error) {
      dbgError('storage', 'transaction(fn) FALLÓ -> ROLLBACK', error)
      try { await this._db.rollbackTransaction() } catch { /* ya abortada */ }
      throw error
    } finally {
      this._inTx = false
    }
  }

  async persist () {}

  async reopen () {
    if (!this._db) {
      await this.open()
      return
    }
    try {
      const { result } = await this._db.isDBOpen()
      if (!result) await this._db.open()
    } catch {
      await this.open()
    }
  }

  async close () {
    if (this._db) {
      await sqliteConnection.closeConnection(DB_NAME, false)
      this._db = null
    }
  }

  /** SEC-01 (v2, off por defecto salvo androidIsEncryption/iosIsEncryption en capacitor.config.ts). */
  async enableEncryption (passphrase) {
    try {
      const inConfig = (await sqliteConnection.isInConfigEncryption()).result
      if (!inConfig) return
      const stored = (await sqliteConnection.isSecretStored()).result
      if (!stored) await sqliteConnection.setEncryptionSecret(passphrase)
    } catch { /* best-effort */ }
  }
}
